/* eslint-disable no-underscore-dangle */
/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-param-reassign */
import moment from 'moment';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CourseTypes,
  IFilteredGroupData,
  IRelatedSkill,
  ISessionTypeModalForm,
  LiveClassTypes,
  SessionAudienceTypes,
  SessionType,
} from 'pages/sessions/interface';
import { IMessageEvent, MessageType } from 'interfaces';
import {
  ISessionCreateResponsePayload,
  CreateSessionState,
  ICreateSessionForm,
  IPaginateFilterPayload,
  ISessionDetails,
  ISessionFilterPayload,
  ISessionFilterResponsePayload,
  BulkCancelSessionParams,
  ISessionDetailsPayload,
  SessionRecordingShareParams,
} from './interface';

const userSettings = {
  allowUpload: true,
  allowMessaging: true,
};

const frequency = {
  startAt: moment().format('HH:mm:ss'),
  endAt: moment().add(1, 'hours').format('HH:mm:ss'),
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  dayOfTheMonth: '1',
  dayOfTheWeek: 'Monday',
  frequency: 'One time',
  classes: [],
};

const availableTimes = [
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Monday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Tuesday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Wednesday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Thursday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Friday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Saturday',
  },
  {
    isAllDay: false,
    isAvailable: false,
    times: [{ from: '', to: '' }],
    classes: [{ from: '', to: '', date: '' }],
    day: 'Sunday',
  },
];

const waitingRoomMedia = {
  fileName: '',
  thumbnailUrl: '',
  contentUrl: '',
  fileType: '',
};

const badge = {
  badgeTitle: '',
  badgeDescription: '',
  badgeImgUrl: '',
};

const baseInformation: ICreateSessionForm = {
  sessionType: 'Digital session',
  courseType: CourseTypes.lsm,
  audienceType: SessionAudienceTypes.public,
  userSettings,
  invitePeople: [],
  title: '',
  description: '',
  message: '',
  highlightMedia: '',
  highlightMediaDisplay: {
    _id: '',
    title: '',
    contentUrl: '',
    fileType: '',
    fileCategory: '',
    thumbnail: '',
    fileSize: 0,
  },
  language: 'English',
  numberOfSessions: 1,
  waitingRoomMedia,
  numberOfParticipants: 2,
  isObserverAllowed: false,
  numberOfObservers: 0,
  forms: [],
  formsList: [],
  media: [],
  mediaList: [],
  groups: [],
  groupsList: [],
  availableTimes,
  sessionDuration: 0,
  frequency,
  fee: '0',
  discount: '0',
  currency: 'AUD',
  subscriberFee: '0',
  subscriberCurrency: 'AUD',
  subscriberDiscount: '0',
  observerFee: '0',
  observerCurrency: 'AUD',
  observerDiscount: '0',
  subscribeObserverFee: '0',
  subscribeObserverCurrency: 'AUD',
  subscribeObserverDiscount: '0',
  badge,
  certificateIssuance: false,
  certificate: undefined,
  modules: [],
  goals: [],
  sessionOutcomes: [],
  relatedSkills: [],
  sessionMeta: {
    liveClassType: LiveClassTypes.interactive,
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(14, 'days').format('YYYY-MM-DD'),
  },
  categories: [],
  prerequisites: [],
  isSequentialModuleNavigationEnabled: false,
  bookedSessions: 0,
  bookedObservers: 0,
  locations: [],
};

export const initialState: CreateSessionState = {
  loading: false,
  modalLoading: false,
  isActive: false,
  groupsResult: {
    result: [],
    total: 0,
  },
  sessionsResult: {
    result: [],
    total: 0,
  },
  session: {
    ...baseInformation,
  },
  pendingSlots: [],
};

export const createSessionSlice = createSlice({
  name: 'feature/session/create-session',
  initialState,
  reducers: {
    resetStatus(state: CreateSessionState) {},
    setSessionType(state: CreateSessionState, action: PayloadAction<ISessionTypeModalForm>) {
      state.session.sessionType = action.payload.sessionType;
      if (action.payload.sessionType === SessionType.digitalSession) {
        state.session.description = 'My digital coaching session allows you to choose the skill or area of your game you want me to help you improve. I will analyse your media, review all parts of your game with personal feedback, share drills and work ons to strengthen your game';
        state.session.message = "This session puts you in the driving seat, allowing you to choose exactly what you want me to help you improve. To get started, you can grab your phone, record the skill or action, game or training footage and upload the recordings in the landscape to the chat tool and I'll get to work. I look forward to working with you.";
      } else if (action.payload.sessionType === SessionType.liveSession) {
        state.session.sessionDuration = 30;
        state.session.description = "Let's catch up on a Live Session. Together we can review your skills through video analysis and annotation. I'll share all my secrets and give you all the work ons to help you improve your game.";
        state.session.message = "Hey, thanks for booking a session with me. To get started, you can grab your phone, record the skill or action, game or training footage and upload the recordings in the landscape to the chat tool and I'll get to work. I look forward to working with you.";
      } else if (action.payload.sessionType === SessionType.liveClass) {
        state.session.description = "Join in to my Live Class. Together we can review your skills through video analysis and annotation. I'll share all my secrets and give you all the work ons to help you improve your game.";
      } else if (action.payload.sessionType === SessionType.event) {
        state.session.description = 'Join my upcoming in-person Event to learn live, ask questions and get real-time takeaways.';
      } else if (action.payload.sessionType === SessionType.content) {
        state.session.message = '-';
      } else if (action.payload.sessionType === SessionType.course
        && state.session.courseType === CourseTypes.lsm) {
        state.session.message = '-';
      }
    },
    fetchSessionDetails(state: CreateSessionState, action: PayloadAction<ISessionDetailsPayload>) {
      state.loading = true;
    },
    fetchSessionDetailsSucceeded(
      state: CreateSessionState,
      action: PayloadAction<ISessionDetails>,
    ) {
      state.session = {
        ...action.payload,
        userSettings: {
          allowUpload: action.payload.userSettings?.allowUpload ?? true,
          allowMessaging: action.payload.userSettings?.allowMessaging ?? true,
        },
        ...((action.payload.sessionType === SessionType.course
          && action.payload.courseType === CourseTypes.lsm)
        || (action.payload.sessionType === SessionType.liveClass
          && !action.payload.userSettings?.allowMessaging)
        || action.payload.sessionType === SessionType.content
          ? { message: '-' }
          : {}),
        highlightMedia: action.payload.highlightMedia ? action.payload.highlightMedia._id : '',
        highlightMediaDisplay: action.payload.highlightMedia
          ? action.payload.highlightMedia
          : {
            _id: '',
            title: '',
            contentUrl: '',
            fileType: '',
            fileCategory: '',
            thumbnail: '',
            fileSize: 0,
          },
        availableTimes: action.payload.availableTimes ?? availableTimes,
        audienceType: action.payload.audienceType,
        frequency: action.payload.frequency
          ? {
            ...action.payload.frequency,
            startAt: moment(action.payload.frequency.startAt).format('HH:mm:ss'),
            endAt: moment(action.payload.frequency.endAt).format('HH:mm:ss'),
            startDate: moment(action.payload.frequency.startDate).format('YYYY-MM-DD'),
            endDate: moment(action.payload.frequency.endDate).format('YYYY-MM-DD'),
          }
          : frequency,
        modules: action.payload.modules.map((module: any) => ({
          ...module,
          session: module.session.map((session: any) => session.id),
          sessionList: module.session,
          form: module.form.map((form: any) => ({ id: form._id, submission: form.submission })),
          formList: module.form,
          content: module.content.map((content: any) => content.id),
          contentList: module.content,
        })),
        fee: action.payload.fee ?? '0.00',
        currency: action.payload.currency ?? 'AUD',
        discount: action.payload.discount ?? '0',
        subscriberFee: action.payload.subscriberFee ?? '0.00',
        subscriberCurrency: action.payload.subscriberCurrency ?? 'AUD',
        subscriberDiscount: action.payload.subscriberDiscount ?? '0',
        subscribeObserverFee: action.payload.subscribeObserverFee ?? '0.00',
        subscribeObserverCurrency: action.payload.subscribeObserverCurrency ?? 'AUD',
        subscribeObserverDiscount: action.payload.subscribeObserverDiscount ?? '0',
        certificateIssuance: action.payload.certificateIssuance ?? false,
        certificate: action.payload.certificate,
        sessionMeta: {
          startDate: action.payload.sessionMeta ? moment(action.payload.sessionMeta.startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          endDate: action.payload.sessionMeta ? moment(action.payload.sessionMeta.endDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          liveClassType: action.payload.sessionMeta ? action.payload.sessionMeta.liveClassType : '-',
        },
        sessionOutcomes: action.payload.sessionOutcomes ?? [],
        relatedSkills: Array.isArray(action.payload.relatedSkills)
          ? action.payload.relatedSkills.map((skill: IRelatedSkill) => skill.id)
          : [],
        categories: Array.isArray(action.payload.categories)
          ? action.payload.categories.map((i) => i.id)
          : [],
        prerequisites: Array.isArray(action.payload?.prerequisites)
          ? action.payload?.prerequisites.map((i) => ({ ...i, _id: i.id }))
          : [],
        bookedSessions: action.payload.bookedSessions ?? 0,
        locations: action.payload?.locations?.map((l) => {
          const startDate = moment(l.from).format('YYYY-MM-DD');
          const endDate = moment(l.to).format('YYYY-MM-DD');
          const isMultiDayEvent = !moment(startDate, 'YYYY-MM-DD').isSame(moment(endDate, 'YYYY-MM-DD'), 'day');
          const from = isMultiDayEvent ? '00:00:00' : moment(l.from).format('HH:mm:ss');
          const to = isMultiDayEvent ? '23:59:00' : moment(l.to).format('HH:mm:ss');
          const date = startDate;
          return ({
            address: l.address,
            description: l.description ?? '',
            startDate,
            endDate,
            from,
            to,
            date,
            isCancel: l.isCancel ?? false,
            isNewLocation: false,
          });
        }) ?? [],
      };
      state.pendingSlots = action.payload.pendingSlots.map(
        (ps) => ({
          from: ps.slot.from,
          to: ps.slot.to,
          date: ps.slot.from,
          meetingId: ps.meetingId,
          location: ps.location,
        }),
      );
      state.loading = false;
    },
    fetchSessionDetailsFailed(state: CreateSessionState) {
      state.loading = false;
    },
    fetchGroups(state: CreateSessionState, action: PayloadAction<IPaginateFilterPayload>) {
      state.modalLoading = true;
    },
    fetchGroupsSucceeded(state: CreateSessionState, action: PayloadAction<IFilteredGroupData>) {
      state.modalLoading = false;
      state.groupsResult = {
        result: action.payload.result.map((item: any) => ({
          id: item._id,
          title: item.title,
          bannerImgUrl: item.bannerImgUrl,
          groupType: item.groupType,
        })),
        total: action.payload.total,
      };
    },
    fetchGroupsFailed(state: CreateSessionState) {
      state.modalLoading = false;
    },
    fetchSessions(state: CreateSessionState, action: PayloadAction<ISessionFilterPayload>) {
      state.loading = true;
    },
    fetchModalSessions(state: CreateSessionState, action: PayloadAction<ISessionFilterPayload>) {
      state.modalLoading = true;
    },
    fetchSessionsSucceeded(
      state: CreateSessionState,
      action: PayloadAction<ISessionFilterResponsePayload>,
    ) {
      state.loading = false;
      state.modalLoading = false;
      state.sessionsResult = {
        result: action.payload.result,
        total: action.payload.total,
      };
    },
    fetchSessionsFailed(state: CreateSessionState) {
      state.loading = false;
      state.modalLoading = false;
    },
    fetchAllSessions(state: CreateSessionState) {
      state.loading = true;
    },
    createSession(state: CreateSessionState, action: PayloadAction<ICreateSessionForm>) {
      state.loading = true;
      state.session = action.payload;
    },
    createSessionSucceeded(
      state: CreateSessionState,
      action: PayloadAction<ISessionCreateResponsePayload>,
    ) {
      state.loading = false;
      if (action.payload.session.audienceType === SessionAudienceTypes.courseOnly) {
        const message: IMessageEvent = {
          type: MessageType.formSubmit,
          data: { ...action.payload.session, moduleIndex: action.payload.moduleIndex },
        };
        window?.opener?.postMessage({ ...message }, window.origin);
        // state.sessionId = action.payload.session.id; // need to figure out a way to
        //  store the session id it could go to edit mode
      } else if (state.session.sessionType === SessionType.course) {
        state.session.modules = [];
      }
    },
    createSessionFailed(state: CreateSessionState) {
      state.loading = false;
    },
    updateSession(
      state: CreateSessionState,
      action: PayloadAction<{ id: string; data: ICreateSessionForm }>,
    ) {
      state.loading = true;
      state.session = action.payload.data;
    },
    updateSessionSucceeded(
      state: CreateSessionState,
      action: PayloadAction<ISessionDetails>,
    ) {
      state.loading = false;
      if (action.payload.audienceType === SessionAudienceTypes.courseOnly) {
        const message: IMessageEvent = {
          type: MessageType.formSubmit,
          data: { ...action.payload, moduleIndex: action.payload.moduleIndex },
        };
        window?.opener?.postMessage({ ...message }, window.origin);
      }
    },
    updateSessionFailed(state: CreateSessionState) {
      state.loading = false;
    },
    bulkCancelSession(state: CreateSessionState, _action: PayloadAction<BulkCancelSessionParams>) {
      state.modalLoading = true;
    },
    bulkCancelSessionSuccess(state: CreateSessionState) {
      state.modalLoading = false;
    },
    bulkCancelSessionFailed(state: CreateSessionState) {
      state.modalLoading = false;
    },
    saveShareSessionRecording(
      state: CreateSessionState,
      _action: PayloadAction<SessionRecordingShareParams>,
    ) {
      state.modalLoading = true;
    },
    saveShareSessionRecordingSuccess(state: CreateSessionState) {
      state.modalLoading = false;
    },
    saveShareSessionRecordingFailed(state: CreateSessionState) {
      state.modalLoading = false;
    },
    reset: () => initialState,
  },
});

export const { actions: createSessionActions } = createSessionSlice;
